import { useEffect, useState } from 'react';
import axios from 'axios';
import LoginModal from '../components/LoginModal';
import Footer from "../components/Footer";
import healthcareHero from "../assets/find-jobs-hero.png";
import hireCuraIcon from "../assets/hirecura-icon.png";
import API_URL from "../config";
import {
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaBriefcase,
  FaClock,
  FaHospital,
  FaUser,
  FaSearch,
  FaBars,
  FaTimes,
  FaHeart,
  FaStethoscope,
  FaUserMd,
  FaPills,
  FaFlask,
  FaHeartbeat,
  FaRunning,
  FaGraduationCap,
  FaXRay,
  FaCapsules,
  FaUserNurse,
  FaShieldAlt,
  FaUserCheck,
  FaHome,
  FaSignOutAlt,
  FaUserCircle,
  FaEnvelope,
} from "react-icons/fa";

import { useNavigate } from 'react-router-dom';

import { useLocation } from "react-router-dom";

const navLinks = [
  { label: "Home", path: "/", icon: FaHome },
  { label: "Find Jobs", path: "/find-jobs", icon: FaBriefcase },
  { label: "For Employers", path: "/for-employers", icon: FaHospital },
  { label: "How It Works", path: "/how-it-works", icon: FaUserCheck },
  { label: "About Us", path: "/about-us", icon: FaHeart },
  { label: "Contact Us", path: "/contact-us", icon: FaEnvelope },
];

export function Navbar({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loggedUser, setLoggedUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  });

  const goToPage = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const logout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    setLoggedUser(null);
    setMobileMenuOpen(false);
    navigate("/");
  };

  const visibleLinks = navLinks.filter(
    (item) => item.label !== "For Employers" || loggedUser?.role !== "candidate"
  );

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-3 md:py-4 flex justify-between items-center">
          {/* ORIGINAL LOGO LAYOUT */}
          <div
            className="cursor-pointer flex items-center gap-3 flex-shrink-0"
            onClick={() => goToPage("/")}
          >
            <img
              src={hireCuraIcon}
              alt="HireCura Icon"
              className="h-9 w-9 sm:h-10 sm:w-10 md:h-12 md:w-12 object-contain"
            />
            <div className="flex flex-col">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight leading-none">
                <span className="text-slate-900">Hire</span>
                <span className="text-teal-600">Cura</span>
              </h1>
              <p className="text-[9px] sm:text-[10px] md:text-xs text-slate-500 font-medium tracking-wide mt-1">
                Where Careers Meet Care.
              </p>
            </div>
          </div>

          {/* ORIGINAL DESKTOP NAVIGATION LAYOUT */}
          <div className="hidden lg:flex items-center gap-7 text-base">
            {visibleLinks.map((item) => (
              <button
                key={item.path}
                type="button"
                onClick={() => goToPage(item.path)}
                className={`relative transition font-medium ${
                  location.pathname === item.path
                    ? "text-teal-600 font-semibold"
                    : "text-gray-700 hover:text-teal-600"
                }`}
              >
                {item.label}
                {location.pathname === item.path && (
                  <span className="absolute -bottom-5 left-0 right-0 h-[3px] bg-teal-600 rounded-full" />
                )}
              </button>
            ))}
          </div>

          {/* ORIGINAL RIGHT SIDE ACCOUNT LAYOUT */}
          <div className="hidden md:flex items-center gap-3">
            {loggedUser ? (
              <>
                <FaUser className="text-[#10243e] text-lg" />
                <span className="hidden xl:block text-sm font-semibold text-gray-700">
                  Welcome, {loggedUser.name}
                </span>
                <button
                  type="button"
                  onClick={() => goToPage(loggedUser.role === "candidate" ? "/candidate-dashboard" : "/institution")}
                  className="border border-teal-600 text-teal-700 px-5 py-2 rounded-full hover:bg-teal-50 transition font-semibold"
                >
                  Dashboard
                </button>
                <button
                  type="button"
                  onClick={logout}
                  className="border border-red-500 text-red-600 px-5 py-2 rounded-full hover:bg-red-50 transition"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <FaUser className="text-[#10243e] text-lg" />
                  <button
                    type="button"
                    onClick={onLogin}
                    className="border border-teal-600 text-teal-700 px-5 py-2 rounded-full hover:bg-teal-50 transition"
                  >
                    Login
                  </button>
                </div>
                <button
                  type="button"
                  onClick={onLogin}
                  className="bg-teal-600 text-white px-6 py-2 rounded-full hover:bg-teal-700 transition font-semibold"
                >
                  Post a Job
                </button>
              </>
            )}
          </div>

          {/* MOBILE MENU BUTTON ONLY: desktop layout remains unchanged */}
          <div className="lg:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="text-2xl text-teal-700 p-2 rounded-lg hover:bg-teal-50 transition"
              aria-label="Open navigation menu"
              aria-expanded={mobileMenuOpen}
            >
              <FaBars />
            </button>
          </div>
        </div>
      </header>

      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        >
          <aside
            className="absolute right-0 top-0 flex h-[100dvh] w-72 max-w-[85vw] flex-col overflow-y-auto overscroll-contain bg-gradient-to-b from-teal-700 to-emerald-800 p-5 text-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
            aria-label="Mobile navigation menu"
          >
            <div className="mb-6 flex items-center justify-between border-b border-white/20 pb-5">
              <button
                type="button"
                onClick={() => goToPage("/")}
                className="flex min-w-0 items-center gap-2.5 text-left"
              >
                <img
                  src={hireCuraIcon}
                  alt="HireCura"
                  className="h-10 w-10 shrink-0 rounded-lg bg-white object-contain p-1"
                />
                <span className="flex min-w-0 flex-col">
                  <span className="whitespace-nowrap text-2xl font-bold leading-none tracking-tight text-white">
                    <span className="!text-slate-900">Hire</span><span className="!text-teal-600">Cura</span>
                  </span>
                  <span className="mt-1 whitespace-nowrap text-[10px] font-medium tracking-wide text-teal-100">
                    Where Careers Meet Care.
                  </span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-xl p-2 text-xl text-white transition hover:bg-white/15"
                aria-label="Close navigation menu"
              >
                <FaTimes />
              </button>
            </div>

            <nav className="flex-1 space-y-2">
              {visibleLinks.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => goToPage(item.path)}
                    className={`flex w-full items-center gap-3 rounded-xl px-4 py-3.5 text-left transition ${
                      active
                        ? "bg-white font-semibold text-teal-700 shadow-sm"
                        : "font-medium text-white hover:bg-white/15"
                    }`}
                  >
                    <Icon className="shrink-0 text-lg" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>

            <div className="mt-5 space-y-3 border-t border-white/20 pt-5">
              {loggedUser ? (
                <>
                  <button
                    type="button"
                    onClick={() => goToPage(loggedUser.role === "candidate" ? "/candidate-dashboard" : "/institution")}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 font-semibold text-teal-700 transition hover:bg-teal-50"
                  >
                    <FaUserCircle /> Dashboard
                  </button>
                  <button
                    type="button"
                    onClick={logout}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-500 px-4 py-3 font-semibold text-white transition hover:bg-red-600"
                  >
                    <FaSignOutAlt /> Logout
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onLogin?.();
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 font-semibold text-teal-700 transition hover:bg-teal-50"
                >
                  <FaUser /> Login
                </button>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

function Homepage() {

  const [jobs, setJobs] = useState([]);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [search, setSearch] = useState('');

  const [professionFilter, setProfessionFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  const [selectedFilter, setSelectedFilter] = useState('All');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(6);
  
useEffect(() => {
  setVisibleCount(6);
}, [
  search,
  selectedFilter,
  professionFilter,
  roleFilter,
  locationFilter
]);

useEffect(() => {
  fetchJobs();
  fetchApplications();
}, []);

useEffect(() => {
  setVisibleCount(6);
}, [search, selectedFilter]);

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API_URL}/jobs/all`);

      if (Array.isArray(res.data)) {
        setJobs(res.data);
      } else {
        setJobs([]);
      }

    } catch (err) {
      console.log(err);
      setJobs([]);
    }
  };
  const fetchApplications = async () => {

  const user = JSON.parse(localStorage.getItem("user"));

  if (!user || user.role !== "candidate") return;

  try {

    const res = await axios.get(
      `${API_URL}/applications/candidate/${user.id}`
    );

    setAppliedJobs(
      res.data.map(app => app.job_id)
    );

  } catch (err) {

    console.log(err);

  }

};
  const getTimeAgo = (date) => {

  const now = new Date();

  const posted = new Date(date);

  const seconds = Math.floor((now - posted) / 1000);

  const minutes = Math.floor(seconds / 60);

  const hours = Math.floor(minutes / 60);

  const days = Math.floor(hours / 24);

  if (minutes < 1) {
    return 'Just now';
  }

  if (minutes < 60) {
    return `${minutes} mins ago`;
  }

  if (hours < 24) {
    return `${hours} hrs ago`;
  }

  if (days === 1) {
    return '1 day ago';
  }

  return `${days} days ago`;
};
const isJobActive = (job) => {
  if (job.status && job.status.toLowerCase() !== 'active') {
    return false;
  }

  if (job.expires_at && new Date(job.expires_at) < new Date()) {
    return false;
  }

  return true;
};

const filteredJobs = jobs
  .filter(isJobActive)
  .filter((job) => {

    const searchText = search.toLowerCase();
    const professionText = professionFilter.toLowerCase();
    const roleText = roleFilter.toLowerCase();
    const locationText = locationFilter.toLowerCase();

    // Existing general search
    const matchesGeneralSearch =
      !searchText ||
      (job.role || '').toLowerCase().includes(searchText) ||
      (job.organization_name || '').toLowerCase().includes(searchText) ||
      (job.company_name || '').toLowerCase().includes(searchText) ||
      (job.location || '').toLowerCase().includes(searchText) ||
      (job.skills_required || '').toLowerCase().includes(searchText) ||
      (job.experience_required || '').toLowerCase().includes(searchText);

    // Profession search
    const matchesProfession =
      !professionText ||
      (job.profession || '').toLowerCase().includes(professionText);

    // Role / Specialization search
    const matchesRole =
      !roleText ||
      (job.role || '').toLowerCase().includes(roleText) ||
      (job.skills_required || '').toLowerCase().includes(roleText);

    // Location search
    const matchesLocation =
      !locationText ||
      (job.location || '').toLowerCase().includes(locationText);

    return (
      matchesGeneralSearch &&
      matchesProfession &&
      matchesRole &&
      matchesLocation
    );
  })
  .filter((job) => {

    if (selectedFilter === 'All') return true;

    if (selectedFilter === 'Remote') {
      return (job.location || '').toLowerCase().includes('remote');
    }

    if (selectedFilter === 'Nurse') {
      return (
        (job.role || '').toLowerCase().includes('nurse') ||
        (job.profession || '').toLowerCase().includes('nursing')
      );
    }

    if (selectedFilter === 'Full Time') {
      return true;
    }

    return true;
  });

const visibleJobs = filteredJobs.slice(0, Math.min(visibleCount, 30));

/*const visibleJobs = filteredJobs.slice(0, visibleCount);*/

  return (

  <div className="bg-[#f5f9f8] min-h-screen overflow-x-hidden">

      {/* 🔥 NAVBAR */}
     {/*<div className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/70 border-b border-gray-200">

        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">*/}
       {/* 🔥 NAVBAR */}
<Navbar onLogin={() => setShowLoginModal(true)} />

      {/* 🔥 HERO SECTION */}
      {/* 🔥 HERO SECTION */}
<div className="relative overflow-hidden bg-gradient-to-br from-white via-[#f3fbfa] to-[#dff5f1]">

  {/* Decorative background */}
  <div className="absolute -right-32 top-0 w-[600px] h-[600px] rounded-full bg-teal-100/50"></div>

  <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-12 md:pt-32 md:pb-24">

    <div className="grid lg:grid-cols-2 gap-7 md:gap-10 items-center">

      {/* LEFT CONTENT */}
      <div className="relative z-10 text-center lg:text-left">

        <p className="text-xs sm:text-sm md:text-base tracking-[0.16em] md:tracking-[0.2em] uppercase text-teal-700 font-semibold mb-3 md:mb-5">
          Healthcare Jobs, Simplified
        </p>

        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-slate-900">

          Find Your Next

          <span className="block text-teal-600">
            Healthcare Opportunity
          </span>

        </h1>

        <p className="mt-4 md:mt-6 text-base sm:text-lg md:text-xl text-slate-600 max-w-xl mx-auto lg:mx-0 leading-relaxed">
          Discover trusted healthcare jobs from hospitals, clinics,
          laboratories and healthcare organizations across India.
        </p>

      </div>


      {/* RIGHT IMAGE */}
      <div className="relative hidden md:flex justify-center lg:justify-end">

        <div className="absolute w-[420px] h-[420px] bg-teal-100 rounded-full -z-10"></div>

        <img
          src={healthcareHero}
          alt="Healthcare Professional"
          className="relative w-full max-w-[500px] h-[500px] object-cover object-top rounded-[45%_45%_0_0] shadow-2xl"
        />

      </div>

    </div>


    {/* SEARCH AREA */}
    <div className="relative z-20 mt-7 md:mt-10">

      <div className="bg-white rounded-2xl md:rounded-3xl p-3 md:p-4 shadow-[0_12px_35px_rgba(15,118,110,0.12)] border border-teal-50">

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_auto] gap-2.5 md:gap-3">

          {/* PROFESSION */}
          <div className="flex items-center min-w-0 border border-gray-200 rounded-2xl px-4 bg-white hover:border-teal-400 transition">

            <FaSearch className="text-teal-600 mr-3 flex-shrink-0" />

            <select
              value={professionFilter}
              onChange={(e) => setProfessionFilter(e.target.value)}
              className="w-full min-w-0 p-3 text-sm md:text-base outline-none text-slate-800 bg-white"
            >

              <option value="">
                Search by Profession
              </option>

              <option value="Doctor">Doctor</option>
              <option value="Nursing">Nursing</option>
              <option value="Pharmacy">Pharmacy</option>
              <option value="Laboratory">Laboratory</option>
              <option value="Radiology">Radiology</option>
              <option value="Physiotherapy">Physiotherapy</option>
              <option value="Medical College Faculty">
                Medical College Faculty
              </option>
              <option value="Nursing & Healthcare Faculty">
                Nursing & Healthcare Faculty
              </option>
              <option value="Hospital Administration">
                Hospital Administration
              </option>
              <option value="Pharmaceutical & Research">
                Pharmaceutical & Research
              </option>
              <option value="Allied Health">
                Allied Health
              </option>
              <option value="Other Healthcare">
                Other Healthcare
              </option>

            </select>

          </div>


          {/* ROLE */}
          <div className="flex items-center min-w-0 border border-gray-200 rounded-2xl px-4 bg-white hover:border-teal-400 transition">

            <FaBriefcase className="text-teal-600 mr-3 flex-shrink-0" />

            <input
              type="text"
              placeholder="Role / Specialization"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full min-w-0 p-3 text-sm md:text-base outline-none text-slate-800"
            />

          </div>


          {/* LOCATION */}
          <div className="flex items-center min-w-0 border border-gray-200 rounded-2xl px-4 bg-white hover:border-teal-400 transition">

            <FaMapMarkerAlt className="text-teal-600 mr-3 flex-shrink-0" />

            <input
              type="text"
              placeholder="Location"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="w-full min-w-0 p-3 text-sm md:text-base outline-none text-slate-800"
            />

          </div>


          {/* SEARCH BUTTON */}
          <button
            type="button"
            onClick={() => {
              document
                .getElementById("job-results")
                ?.scrollIntoView({
                  behavior: "smooth"
                });
            }}
            className="bg-teal-600 text-white px-5 md:px-8 py-3 rounded-xl md:rounded-2xl hover:bg-teal-700 transition font-semibold text-base md:text-lg flex items-center justify-center gap-2 min-h-[48px] md:min-h-[56px]"
          >

            <FaSearch />

            Search Jobs

          </button>

        </div>

      </div>


      {/* POPULAR SEARCHES */}
      <div className="mt-4 md:mt-5 flex flex-wrap items-center justify-center lg:justify-start gap-2">

        <span className="w-full sm:w-auto text-center sm:text-left text-xs sm:text-sm font-semibold text-slate-700 sm:mr-2 mb-1 sm:mb-0">
          Popular Healthcare Searches:
        </span>

        {[
          "Doctors",
          "Nurses",
          "Pharmacists",
          "Lab Technicians",
          "Physiotherapists",
          "Healthcare Faculty"
        ].map((item) => (

          <button
            key={item}
            type="button"
            onClick={() => setRoleFilter(item)}
            className="px-3 py-1.5 md:px-4 md:py-2 bg-white border border-teal-100 rounded-full text-xs sm:text-sm text-slate-700 hover:border-teal-500 hover:text-teal-600 hover:shadow-sm transition"
          >
            {item}
          </button>

        ))}

      </div>

    </div>

  </div>


</div>
{/* EXPLORE BY PROFESSION */}
{/* EXPLORE BY PROFESSION */}
<section className="bg-white py-9 md:py-14">
  <div className="max-w-7xl mx-auto px-4 sm:px-6">

    {/* Heading */}
    <div className="flex items-center justify-between gap-3 mb-5 md:mb-7">

      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#10243e]">
        Explore by Profession
      </h2>

      <button
        type="button"
        onClick={() => {
          setProfessionFilter("");
          setRoleFilter("");
          setLocationFilter("");
          setSearch("");
          setSelectedFilter("All");

          setTimeout(() => {
            document
              .getElementById("job-results")
              ?.scrollIntoView({ behavior: "smooth" });
          }, 100);
        }}
        className="shrink-0 text-sm sm:text-base text-teal-600 font-semibold hover:text-teal-700 flex items-center gap-1.5"
      >
        View All
        <span className="text-lg">→</span>
      </button>

    </div>


    {/* Profession Cards */}
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4">

      {[
        {
          name: "Doctors",
          value: "Doctor",
          icon: FaStethoscope
        },
        {
          name: "Nursing",
          value: "Nursing",
          icon: FaUserMd
        },
        {
          name: "Pharmacy",
          value: "Pharmacy",
          icon: FaPills
        },
        {
          name: "Laboratory",
          value: "Laboratory",
          icon: FaFlask
        },
        {
          name: "Radiology",
          value: "Radiology",
          icon: FaHeartbeat
        },
        {
          name: "Physiotherapy",
          value: "Physiotherapy",
          icon: FaRunning
        },
        {
          name: "Healthcare Faculty",
          value: "Nursing & Healthcare Faculty",
          icon: FaGraduationCap
        },
        {
          name: "Hospital Administration",
          value: "Hospital Administration",
          icon: FaHospital
        }
      ].map((profession) => {

        const ProfessionIcon = profession.icon;

        return (
          <button
            key={profession.value}
            type="button"
            onClick={() => {

              setProfessionFilter(profession.value);
              setRoleFilter("");
              setLocationFilter("");
              setSearch("");
              setSelectedFilter("All");

              setTimeout(() => {
                document
                  .getElementById("job-results")
                  ?.scrollIntoView({
                    behavior: "smooth"
                  });
              }, 100);

            }}
            className="group bg-[#f8fbfb] border border-gray-100 rounded-xl p-3 sm:p-4 md:p-5 text-center hover:bg-[#eefaf8] hover:border-teal-200 hover:shadow-sm transition-all duration-200"
          >

            {/* ICON */}
            <div className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 md:mb-3 rounded-full bg-[#e5f8f5] flex items-center justify-center group-hover:scale-105 transition">

              <ProfessionIcon
                className="text-lg md:text-xl text-teal-600"
              />

            </div>

            {/* NAME */}
            <p className="text-xs sm:text-sm font-semibold text-[#10243e] leading-tight">
              {profession.name}
            </p>

          </button>
        );

      })}

    </div>

  </div>
</section>

      {/* 🔥 JOB SECTION */}
     <div
  id="job-results"
  className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-16"
>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6 mb-6 md:mb-10">
        

  {/* SECTION TITLE */}
  <div>

    <p className="text-xs sm:text-sm uppercase tracking-widest text-teal-600 font-semibold mb-2">
      
      Explore Opportunities
    </p>

    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900">
      Latest Healthcare Jobs
    </h2>

    <p className="text-sm sm:text-base text-gray-500 mt-2">
      Discover the latest opportunities from healthcare employers.
    </p>

    <p className="text-xs sm:text-sm text-gray-400 mt-2">
      Showing {visibleJobs.length} of {filteredJobs.length} active jobs
    </p>

  </div>


  {/* FILTERS */}
  <div className="flex flex-wrap gap-2">

    {['All', 'Remote', 'Full Time', 'Nurse'].map((filter) => (

      <button
        key={filter}
        onClick={() => setSelectedFilter(filter)}
        className={`rounded-full px-4 py-2 md:px-5 md:py-2.5 text-xs sm:text-sm font-semibold transition ${
          selectedFilter === filter
            ? 'bg-teal-600 text-white shadow-sm'
            : 'bg-white border border-gray-200 text-gray-700 hover:border-teal-400 hover:text-teal-600'
        }`}
      >
        {filter}
      </button>

    ))}

  </div>

</div>

        {filteredJobs.length === 0 ? (

          <div className="text-center py-14 md:py-20 px-4 bg-white rounded-2xl md:rounded-3xl shadow">
            <h3 className="text-xl md:text-2xl font-bold text-gray-700">
              No Jobs Found
            </h3>

            <p className="text-sm sm:text-base text-gray-500 mt-2">
              Try searching with another keyword.
            </p>
          </div>

        ) : (

          <div className="space-y-4">

  {visibleJobs.map((job) => (

    <div
      key={job.id}
      onClick={() => navigate(`/job/${job.id}`)}
      className="group bg-white border border-gray-200 rounded-2xl px-3.5 py-3.5 sm:px-5 sm:py-4 md:px-6 md:py-5 hover:border-teal-200 hover:shadow-md transition-all duration-200 cursor-pointer"
    >

      <div className="flex items-start sm:items-center gap-3 sm:gap-4 md:gap-6">

        {/* COMPANY LOGO */}
        <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl border border-gray-100 bg-white flex-shrink-0 overflow-hidden flex items-center justify-center">

          <img
            src={
              job.company_logo
                ? `${API_URL.replace('/api', '')}/${job.company_logo}`
                : `https://ui-avatars.com/api/?name=${
                    job.company_name || job.institution_name || "Healthcare"
                  }&background=e6f7f4&color=008f83&bold=true`
            }
            alt="Company Logo"
            className="w-full h-full object-contain"
          />

        </div>


        {/* JOB INFORMATION */}
        <div className="flex-1 min-w-0">

          <h3 className="text-base sm:text-lg md:text-xl font-bold capitalize text-[#10243e] break-words leading-snug">
            {job.role}
          </h3>

          <div className="mt-2 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-x-4 gap-y-1.5 sm:gap-y-2 text-xs sm:text-sm text-gray-500">

            <span className="flex items-center gap-2">
              <FaHospital className="text-[#10243e]" />
              {job.company_name ||
                job.institution_name ||
                "Healthcare Institution"}
            </span>

            <span className="flex items-center gap-2">
              <FaMapMarkerAlt className="text-[#10243e]" />
              {job.location || "Location not specified"}
            </span>

            <span className="flex items-center gap-2">
              <FaBriefcase className="text-[#10243e]" />
              {job.experience_required
                ? `${job.experience_required} Years`
                : "Experience not specified"}
            </span>

            <span className="flex items-center gap-2">
              <FaGraduationCap className="text-[#10243e]" />
              {job.profession || "Healthcare"}
            </span>

          </div>

        </div>


        {/* RIGHT SIDE */}
        <div className="hidden md:flex items-center gap-8 flex-shrink-0">

          <span className="bg-[#e3f8f3] text-[#008f83] px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap">
            {job.profession || "Healthcare"}
          </span>

          <span className="text-sm text-gray-500 whitespace-nowrap">
            {getTimeAgo(job.created_at)}
          </span>

          <div className="w-10 h-10 rounded-full flex items-center justify-center group-hover:bg-[#e6f7f4] transition">
            <span className="text-2xl text-[#10243e] group-hover:text-[#008f83] transition">
              →
            </span>
          </div>

        </div>


        {/* MOBILE ARROW */}
        <div className="md:hidden flex-shrink-0">

          <div className="w-9 h-9 rounded-full bg-[#e6f7f4] flex items-center justify-center">
            <span className="text-xl text-[#008f83]">
              →
            </span>
          </div>

        </div>

      </div>


      {/* MOBILE META */}
      <div className="md:hidden mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">

        <span className="bg-[#e3f8f3] text-[#008f83] px-2.5 py-1.5 rounded-lg text-[11px] sm:text-xs font-semibold">
          {job.profession || "Healthcare"}
        </span>

        <span className="text-[11px] sm:text-xs text-gray-500">
          {getTimeAgo(job.created_at)}
        </span>

      </div>

    </div>

  ))}

</div>

        )}
      {visibleCount < filteredJobs.length && visibleCount < 30 && (
  <div className="mt-7 md:mt-10 flex justify-center">
    <button
      onClick={() => setVisibleCount((previous) => previous + 6)}
      className="rounded-full border px-6 py-2.5 md:px-7 md:py-3 font-semibold text-sm md:text-base text-teal-600 transition hover:bg-teal-600 hover:text-white"
    > 
      Load More Jobs
    </button>
  </div>
)}

{filteredJobs.length > 30 && visibleCount >= 30 && (
  <div className="mt-7 md:mt-10 flex justify-center">
    <button
      onClick={() => navigate('/jobs')}
      className="rounded-full bg-blue-600 px-7 py-3 font-semibold text-white transition hover:bg-blue-700"
    >
      View All Jobs
    </button>
  </div>
)}

      </div>
      
      {/* WHY CHOOSE HIRECURA */}
<section className="bg-white py-9 md:py-14">
  <div className="max-w-7xl mx-auto px-4 sm:px-6">

    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#10243e] mb-6 md:mb-8">
      Why Choose HireCura?
    </h2>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">

      {/* Verified Employers */}
      <div className="flex items-center gap-4">

        <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-[#e8faf7] flex items-center justify-center flex-shrink-0">
          <FaUserCheck className="text-2xl md:text-4xl text-teal-600" />
        </div>

        <div>
          <h3 className="font-bold text-[#10243e] text-base">
            Verified Employers
          </h3>

          <p className="text-gray-500 text-sm mt-1">
            Trusted Hospitals and Clinics
          </p>
        </div>

      </div>


      {/* Secure Platform */}
      <div className="flex items-center gap-4">

        <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-[#e8faf7] flex items-center justify-center flex-shrink-0">
          <FaShieldAlt className="text-2xl md:text-4xl text-teal-600" />
        </div>

        <div>
          <h3 className="font-bold text-[#10243e] text-base">
            Secure Platform
          </h3>

          <p className="text-gray-500 text-sm mt-1">
            Your Data is Safe and Protected
          </p>
        </div>

      </div>


      {/* Healthcare Focused */}
      <div className="flex items-center gap-4">

        <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-[#e8faf7] flex items-center justify-center flex-shrink-0">
          <FaHeartbeat className="text-2xl md:text-4xl text-teal-600" />
        </div>

        <div>
          <h3 className="font-bold text-[#10243e] text-base">
            Healthcare Focused
          </h3>

          <p className="text-gray-500 text-sm mt-1">
            Jobs Only in Healthcare
          </p>
        </div>

      </div>


      {/* Free Registration */}
      <div className="flex items-center gap-4">

        <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-[#e8faf7] flex items-center justify-center flex-shrink-0">
         <FaUserCheck className="text-2xl md:text-4xl text-teal-600" />
        </div>

        <div>
          <h3 className="font-bold text-[#10243e] text-base">
            Free Registration
          </h3>

          <p className="text-gray-500 text-sm mt-1">
            For All Job Seekers
          </p>

          <p className="text-gray-500 text-sm">
            No Hidden Charges
          </p>
        </div>

      </div>

    </div>

  </div>
</section>

      {/* 🔥 CTA SECTION */}
      <div className="bg-gradient-to-r from-teal-600 to-emerald-700 py-12 md:py-20 text-white text-center px-4 sm:px-6">

        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight">
          Ready to Advance Your Healthcare Career?
        </h2>

        <p className="mt-3 md:mt-4 text-sm sm:text-base md:text-lg text-teal-100">
          Join thousands of healthcare professionals finding opportunities daily.
        </p>

        <div className="mt-6 md:mt-8 flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 flex-wrap">

          {/* <button
            onClick={() => setShowLoginModal(true)}
            className="bg-white text-teal-700 px-8 py-3 rounded-full font-semibold"
          >
            Find Jobs
          </button>

          <button
            onClick={() => setShowLoginModal(true)}
            className="border border-white px-8 py-3 rounded-full font-semibold"
          >
            Hire Professionals
          </button> */}
          <button
            onClick={() => setShowLoginModal(true)}
            className="w-full sm:w-auto border border-white bg-transparent text-white hover:bg-white hover:text-teal-700 px-6 md:px-8 py-3 rounded-full font-semibold transition-colors duration-300"
          >
            Find Jobs
          </button>

          <button
            onClick={() => setShowLoginModal(true)}
            className="w-full sm:w-auto border border-white bg-transparent text-white hover:bg-white hover:text-teal-700 px-6 md:px-8 py-3 rounded-full font-semibold transition-colors duration-300"
          >
            Hire Professionals
          </button>

        </div>

      </div>

      <LoginModal
    isOpen={showLoginModal}
    onClose={() => setShowLoginModal(false)}
/>

<Footer />

</div>
);
}

export default Homepage;